// Inline-expand variant of the SOW form — wraps SowFormFields in a write-mode
// container rendered inside an expanded register row. Auto-focuses the first
// editable input on mount; calls `onClosed` on unmount so the parent can
// restore focus to the row's toggle button.
//
// Critical: `originalValue` and `number` are read-only here. The standard
// PUT path does not pass through the sentinel uniqueness check (sentinel is
// only written on create), so allowing number mutation via PUT would silently
// break uniqueness. Original value follows the same rule — typo repair flows
// through the dedicated audit-logged correction endpoint, never PUT.
//
// When approved variations affect this SOW, the original-value
// banner annotates the current-value composition (`$120,000 + $20,000
// from VAR-2026-002`) so the contract manager can see the audit trail
// at the point of inspection. Variation impact does not mutate the
// SOW's stored originalValue.

import { useEffect, useMemo, useRef, useState } from "react";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import SowFormFields from "./SowFormFields";
import {
  parseOriginalValue,
  stateFromSow,
  toUpdateSowInput,
  type SowFieldErrors,
  type SowFormState,
} from "./sowFormState";
import { isValidIsoDate } from "../shared/isoDateValidation";
import { gstSuffix } from "../../../../utils/gstSuffix";
import { formatSowCurrency } from "./formatSowCurrency";
import {
  approvedSowVariations,
  currentSowValue,
} from "./contractValueDerivation";
import type { Sow, UpdateSowInput, Variation } from "../../../../types";

function validate(state: SowFormState): SowFieldErrors {
  const errors: SowFieldErrors = {};
  if (!state.name.trim()) errors.name = "Name is required";
  if (!isValidIsoDate(state.startDate)) {
    errors.startDate = "Enter a valid start date";
  }
  if (!isValidIsoDate(state.endDate)) {
    errors.endDate = "Enter a valid end date";
  }
  if (
    !errors.startDate &&
    !errors.endDate &&
    state.endDate < state.startDate
  ) {
    errors.endDate = "End date must be on or after start date";
  }
  // originalValue is locked in this surface; we don't validate user input
  // for it because the field is disabled. We do verify the parsed source
  // is positive so a malformed legacy row surfaces visibly.
  if (parseOriginalValue(state.originalValue) <= 0) {
    errors.originalValue = "Stored original value is invalid";
  }
  return errors;
}

function hasErrors(errors: SowFieldErrors): boolean {
  return Object.values(errors).some((v) => v !== undefined);
}

interface SowInlineExpandProps {
  item: Sow;
  contractId: string;
  gstApplies: boolean | undefined;
  /** Variations on this contract — used to derive the current-value
   *  annotation when approved sow-scope variations affect this SOW.
   *  Defaults to empty so consumers that haven't yet wired the
   *  variation feed render the original-value-only view cleanly. */
  variations?: Variation[];
  onUpdate: (input: UpdateSowInput) => Promise<unknown>;
  onCancel: () => void;
  onClosed?: () => void;
  isSubmitting?: boolean;
}

export default function SowInlineExpand({
  item,
  contractId,
  gstApplies,
  variations = [],
  onUpdate,
  onCancel,
  onClosed,
  isSubmitting,
}: SowInlineExpandProps) {
  const [state, setState] = useState<SowFormState>(() => stateFromSow(item));
  const [errors, setErrors] = useState<SowFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const first = rootRef.current?.querySelector<HTMLElement>(
      'input:not([type="checkbox"]):not([type="radio"]):not([disabled]), select:not([disabled]), textarea:not([disabled])',
    );
    first?.focus();
    return () => {
      onClosed?.();
    };
  }, [onClosed]);

  const handleChange = (patch: Partial<SowFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    const keys = Object.keys(patch);
    setErrors((prev) => {
      const next = { ...prev };
      for (const k of keys) {
        delete next[k as keyof SowFieldErrors];
      }
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (hasErrors(v)) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      await onUpdate(toUpdateSowInput(state, contractId, item.version));
      onCancel();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save SOW.",
      );
    }
  };

  const original = formatSowCurrency(item.originalValue, gstApplies);
  const basis = gstSuffix(gstApplies);

  const sowVariations = useMemo(
    () => approvedSowVariations(item.id, variations),
    [item.id, variations],
  );
  const current = useMemo(
    () => currentSowValue(item, variations),
    [item, variations],
  );
  const formattedCurrent = formatSowCurrency(current, gstApplies);

  return (
    <div ref={rootRef} className="p-4">
      <div className="mb-4 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <p className="text-caption text-slate-500 dark:text-slate-400">
              Original value
            </p>
            <p className="text-body-sm text-slate-900 dark:text-slate-100">
              <span
                className="tabular-nums"
                data-testid="sow-inline-original-value"
              >
                {original.value}
              </span>{" "}
              <span
                className="text-caption text-slate-500 dark:text-slate-400"
                data-testid="sow-inline-original-value-basis"
              >
                {basis}
              </span>
            </p>
          </div>
          <div>
            <p className="text-caption text-slate-500 dark:text-slate-400">
              Current value
            </p>
            <p className="text-body-sm text-slate-900 dark:text-slate-100">
              <span
                className="tabular-nums"
                data-testid="sow-inline-current-value"
              >
                {formattedCurrent.value}
              </span>{" "}
              <span className="text-caption text-slate-500 dark:text-slate-400">
                {basis}
              </span>
            </p>
            {sowVariations.length > 0 && (
              // GST basis is inherited from the Current value line above
              // (visual proximity); we don't repeat the suffix per
              // component to keep the annotation compact. Same pattern
              // as the stacked-bar legend, where one trailing basis
              // annotation scopes the whole legend rather than tagging
              // each abbreviated dollar figure individually.
              <p
                data-testid="sow-inline-variations-annotation"
                className="text-caption text-slate-500 dark:text-slate-400 mt-1"
              >
                {original.value}
                {sowVariations.map((v) => {
                  const sign = (v.valueImpact ?? 0) >= 0 ? "+" : "−";
                  const abs = Math.abs(v.valueImpact ?? 0);
                  const formatted = `$${abs.toLocaleString("en-AU", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}`;
                  return ` ${sign} ${formatted} from ${v.number}`;
                })}
              </p>
            )}
          </div>
        </div>
        <p className="text-caption text-slate-500 dark:text-slate-400 mt-3">
          Once saved, the original value cannot be edited directly. Raise
          a variation to adjust the value later, or use Correct value to
          repair a data-entry typo (audit-logged).
        </p>
        <p className="text-caption text-slate-500 dark:text-slate-400 mt-1">
          Deliverables &amp; milestones scoped to this SOW will
          be visible here.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SowFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`sow-edit-${item.id}`}
          mode="edit"
          gstApplies={gstApplies}
          disabled={isSubmitting}
          suppressOriginalValueHelper
          sourceStatus={item.status}
        />
        {submitError && <Alert variant="error">{submitError}</Alert>}
        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            Save changes
          </Button>
        </div>
      </form>
    </div>
  );
}
